<script setup lang="ts">
import type {
  ColDef,
  ColGroupDef,
  GridApi,
  GridReadyEvent,
  IsFullWidthRowParams,
  PostSortRowsParams,
  RowClassParams,
  RowClickedEvent,
  RowHeightParams,
  RowSelectionOptions,
  SortChangedEvent,
} from "ag-grid-community";
import { AgGridVue } from "ag-grid-vue3";
import type { Component } from "vue";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { GRID_FIT_MIN_WIDTH } from "./composables/useGridColumnDefaults.ts";
import "./css/AppGrid.css";
import NoRowsOverlay from "./NoRowsOverlay.vue";

type DomLayoutMode = "autoHeight" | "normal" | "print";
interface AppGridProperties {
  columnDefs: (ColDef | ColGroupDef)[];
  rowData: unknown[];
  defaultColDef: ColDef;
  rowSelection?: RowSelectionOptions;
  selectionColumnDef?: ColDef;
  domLayout?: DomLayoutMode;
  isFullWidthRow?: (parameters: IsFullWidthRowParams) => boolean;
  fullWidthCellRenderer?: Component;
  getRowId?: (parameters: { data: unknown }) => string;
  getRowHeight?: (parameters: RowHeightParams) => number;
  getRowClass?: (parameters: RowClassParams) => string | string[] | undefined;
  noRowsOverlayComponentParams?: Record<string, unknown>;
  autoSizeColumns?: boolean;
  defaultRowHeight?: number;
  maxRows?: number;
  minGridHeight?: number;
  fillContainer?: boolean;
  postSortRows?: (params: PostSortRowsParams) => void;
  showTopBorder?: boolean;
  enableColumnMenu?: boolean;
  suppressColumnMovement?: boolean;
  suppressColumnFilter?: boolean;
  enableCellTextSelection?: boolean;
  suppressCellSelection?: boolean;
  id: string;
}
const DEFAULT_ROW_HEIGHT = 40;
const DEFAULT_MIN_GRID_HEIGHT = 200;
const props = withDefaults(defineProps<AppGridProperties>(), {
  rowSelection: () => ({
    mode: "singleRow",
    checkboxes: false,
    enableClickSelection: true,
  }),
  domLayout: "normal",
  isFullWidthRow: () => false,
  getRowHeight: () => DEFAULT_ROW_HEIGHT,
  defaultRowHeight: DEFAULT_ROW_HEIGHT,
  maxRows: 10,
  minGridHeight: DEFAULT_MIN_GRID_HEIGHT,
  enableCellTextSelection: true,
  suppressCellSelection: false,
  autoSizeColumns: true,
});

const gridApi = ref<GridApi>();
const gridContainer = ref<HTMLElement>();
const resizeTimer = ref<ReturnType<typeof globalThis.setTimeout>>();
const resizeObserver = ref<ResizeObserver>();
const pendingLayoutFrame = ref<number>();
const rowDataComputed = computed(() => props.rowData);

const mergedDefaultColDef = computed(() => ({
  ...props.defaultColDef,
  ...(props.suppressColumnFilter ? { filter: false } : {}),
  suppressHeaderMenuButton: !props.enableColumnMenu,
}));
watch(rowDataComputed as object, () => {
  resizeGrid();
  scheduleLayout();
});

watch(
  () => props.columnDefs,
  () => {
    scheduleLayout();
  },
  { deep: true }
);

function fitColumnsToGrid(): void {
  gridApi.value?.sizeColumnsToFit({ defaultMinWidth: GRID_FIT_MIN_WIDTH });
}

function scheduleLayout(): void {
  if (
    !props.autoSizeColumns ||
    !gridApi.value ||
    gridApi.value.isDestroyed() ||
    !gridContainer.value
  ) {
    return;
  }

  if (pendingLayoutFrame.value !== undefined) {
    globalThis.cancelAnimationFrame(pendingLayoutFrame.value);
  }

  pendingLayoutFrame.value = globalThis.requestAnimationFrame(() => {
    const api = gridApi.value;
    if (!api || api.isDestroyed() || !gridContainer.value) {
      return;
    }

    fitColumnsToGrid();
    pendingLayoutFrame.value = undefined;
  });
}

function attachResizeObserver(): void {
  if (
    resizeObserver.value !== undefined ||
    gridContainer.value === undefined ||
    typeof ResizeObserver === "undefined"
  ) {
    return;
  }

  resizeObserver.value = new ResizeObserver(() => {
    scheduleLayout();
  });
  resizeObserver.value.observe(gridContainer.value);
}

function refreshOverlayState(): void {
  if (!gridApi.value) {
    return;
  }

  if (gridApi.value.isDestroyed()) {
    return;
  }

  gridApi.value.setGridOption(
    "noRowsOverlayComponentParams",
    props.noRowsOverlayComponentParams
  );

  if (props.rowData.length === 0) {
    gridApi.value.showNoRowsOverlay();
    return;
  }

  gridApi.value.hideOverlay();
}

watch(
  [rowDataComputed, () => props.noRowsOverlayComponentParams],
  () => {
    refreshOverlayState();
  },
  { deep: true }
);

const resizeGrid = (): void => {
  if (
    props.fillContainer ||
    props.domLayout === "autoHeight" ||
    !gridApi.value
  ) {
    return;
  }
  const api = gridApi.value;
  if (resizeTimer.value !== undefined) {
    globalThis.clearTimeout(resizeTimer.value);
  }
  resizeTimer.value = globalThis.setTimeout(() => {
    if (api.isDestroyed()) {
      return;
    }
    const gridElement = document.querySelector<HTMLElement>(`#${props.id}`);
    if (gridElement) {
      const defaultRowHeight = props.getRowHeight({} as RowHeightParams);
      const maxGridHeight = props.maxRows * defaultRowHeight;
      let totalRowsHeight = 0;
      api.forEachNode((node) => {
        const rowHeightParams: RowHeightParams = {
          data: node.data as unknown,
          context: undefined,
          node,
          api,
        };
        const height = props.getRowHeight(rowHeightParams);
        totalRowsHeight += height;
      });
      const gridHeight = Math.max(
        props.minGridHeight,
        Math.min(maxGridHeight, totalRowsHeight)
      );
      gridElement.style.height = `${String(gridHeight + 42)}px`;
    }
    resizeTimer.value = undefined;
  }, 300);
};
const emit = defineEmits<{
  (event: "grid-ready", payload: GridReadyEvent): void;
  (event: "body-scroll", payload: { left: number; top: number }): void;
  (event: "selection-changed"): void;
  (event: "sort-changed", payload: SortChangedEvent): void;
  (event: "row-clicked", payload: RowClickedEvent): void;
}>();

function onSelectionChanged(): void {
  emit("selection-changed");
}

function onBodyScroll(payload: { left: number; top: number }): void {
  emit("body-scroll", payload);
}

function onSortChanged(event: SortChangedEvent): void {
  emit("sort-changed", event);
}

function onRowClicked(event: RowClickedEvent): void {
  emit("row-clicked", event);
}

function onFirstDataRendered(): void {
  scheduleLayout();
}

function onGridReady(event: GridReadyEvent): void {
  gridApi.value = event.api;
  if (props.autoSizeColumns) {
    fitColumnsToGrid();
  }
  attachResizeObserver();
  scheduleLayout();
  resizeGrid();
  refreshOverlayState();
  emit("grid-ready", event);
}

onBeforeUnmount(() => {
  if (resizeTimer.value !== undefined) {
    globalThis.clearTimeout(resizeTimer.value);
    resizeTimer.value = undefined;
  }

  resizeObserver.value?.disconnect();
  resizeObserver.value = undefined;

  if (pendingLayoutFrame.value !== undefined) {
    globalThis.cancelAnimationFrame(pendingLayoutFrame.value);
    pendingLayoutFrame.value = undefined;
  }

  gridApi.value = undefined;
});
</script>

<template>
  <div
    ref="gridContainer"
    :class="[
      'po-grid min-w-0',
      {
        'h-full': props.fillContainer,
        'po-grid-show-top-border': props.showTopBorder,
      },
    ]"
  >
    <AgGridVue
      :id="props.id"
      :class="{ 'h-full': props.fillContainer }"
      :column-defs="props.columnDefs"
      :row-data="props.rowData"
      :default-col-def="mergedDefaultColDef"
      :row-selection="props.rowSelection"
      :selection-column-def="props.selectionColumnDef"
      :dom-layout="props.domLayout"
      :suppress-column-move-animation="true"
      :suppress-movable-columns="props.suppressColumnMovement"
      :is-full-width-row="props.isFullWidthRow"
      :full-width-cell-renderer="props.fullWidthCellRenderer"
      :get-row-id="props.getRowId"
      :get-row-height="props.getRowHeight"
      :get-row-class="props.getRowClass"
      :post-sort-rows="props.postSortRows"
      :no-rows-overlay-component="NoRowsOverlay"
      :no-rows-overlay-component-params="props.noRowsOverlayComponentParams"
      :header-height="props.defaultRowHeight"
      :enable-cell-text-selection="props.enableCellTextSelection"
      :suppress-cell-selection="props.suppressCellSelection"
      :suppress-horizontal-scroll="props.autoSizeColumns"
      @grid-ready="onGridReady"
      @first-data-rendered="onFirstDataRendered"
      @body-scroll="onBodyScroll"
      @selection-changed="onSelectionChanged"
      @sort-changed="onSortChanged"
      @row-clicked="onRowClicked"
    />
  </div>
</template>
